// app/features/ai-workflows/components/UseCaseActionsDropdownComponent.tsx
"use client";

import { Loader2, MoreVertical, Trash2 } from "lucide-react";
import { useState } from "react";

import { HTFButton } from "@/app/shared/components/button/HTFButtonComponent";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { PersonalizedUseCase } from "../personalized-use-case.interface";

interface UseCaseActionsDropdownProps {
  useCase: PersonalizedUseCase;
  onDelete: (useCaseId: string) => Promise<void>;
  isDeleting?: boolean;
}

export const UseCaseActionsDropdownComponent = ({
  useCase,
  onDelete,
  isDeleting = false,
}: UseCaseActionsDropdownProps) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await onDelete(useCase.id);
      setShowDeleteDialog(false);
    } catch (error) {
      // Error is handled by the mutation hook
      console.error("[DELETE] Delete failed:", error);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="p-3 rounded-md hover:bg-gray-100 transition-colors cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            aria-label="More actions"
          >
            <MoreVertical size={20} className="text-gray-500" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-56 bg-white rounded-xl shadow-lg py-2 z-50"
        >
          <DropdownMenuItem
            onClick={handleDeleteClick}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-display font-medium text-orange hover:bg-orange/10 text-left transition-colors cursor-pointer"
            disabled={isDeleting}
          >
            {isDeleting ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Trash2 size={16} />
            )}
            {isDeleting ? "Deleting..." : "Delete AI Workflow"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-white !border-none shadow-2xl">
          <DialogHeader>
            <DialogTitle>Delete AI Workflow</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this AI workflow? This action
              cannot be undone.
              {useCase.title && (
                <span className="block mt-2 font-medium text-gray-900">
                  "{useCase.title}"
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <HTFButton
              variant="secondary-outline"
              onClick={handleCancelDelete}
              disabled={isDeleting}
            >
              Cancel
            </HTFButton>
            <HTFButton
              variant="destructive"
              onClick={handleConfirmDelete}
              isLoading={isDeleting}
              disabled={isDeleting}
            >
              Delete
            </HTFButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
